import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { ErrorList, InputLabelWrapper, } from '@guildeducationinc/recess';
import SearchIcon from '@guildeducationinc/recess/icons/SearchIcon';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField } from '@mui/material';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useLocale } from '../../hooks';
import styles from './MuiSingleSelect.module.scss';
export const MuiSingleSelect = ({ label, hideLabel, placeholder, value, onChange, options, className, errors, disabled, additionalInfo, helpText, required, renderOption = (props, option) => (_createElement("li", { ...props, key: option.value }, option.label)), getOptionSelectedLabel = option => option.label || '', showSearchIcon = false, disablePortal = false, onSearch, onScrollEnd, loading, inputValue, paginationLoading = false, disableWeglot = false, }) => {
    const { t } = useLocale();
    const inputId = uniqueId('select-input-');
    // Memoize the filter function to prevent infinite re-renders
    const filterOptions = useCallback((options) => {
        // Don't filter out any options - we handle filtering on the server side
        // This ensures our loading indicator always shows up
        return options;
    }, []);
    const handleSelect = (_, selection) => {
        // Don't allow selection of the loading indicator
        if (selection && selection.isLoadingIndicator) {
            return;
        }
        const value = selection?.value || '';
        // Trigger onChange event
        onChange({
            target: { value },
            selection,
        });
    };
    const getOpt = (value) => {
        return options.find(opt => opt.value === value) || null;
    };
    // Create enhanced options list with loading indicator if needed
    const enhancedOptions = [...options];
    if (paginationLoading) {
        enhancedOptions.push({
            value: '__loading__',
            label: 'Loading...',
            isLoadingIndicator: true,
        });
    }
    useEffect(() => {
        // Only reset if value is not empty and not in options
        if (value !== undefined && value !== '' && !options.some(option => option.value === value)) {
            onChange({
                target: { value: '' },
                selection: null,
            });
        }
    }, [options, value, onChange]);
    const customRenderOption = (props, option) => {
        // Render loading indicator
        if (option.isLoadingIndicator) {
            return (_createElement("li", { ...props, key: 'loading-indicator', style: { ...props.style, pointerEvents: 'none' } },
                _jsxs(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1, width: '100%', children: [_jsx(CircularProgress, { size: 20 }), _jsx("span", { style: { marginLeft: 8 }, children: t('Loading more options...') })] })));
        }
        // Use the custom renderOption prop if provided, otherwise use default
        return renderOption(props, option);
    };
    return (_jsxs("div", { className: classNames(className, 'flex flex-col'), children: [_jsx(InputLabelWrapper, { hideLabel: hideLabel, label: label, inputId: inputId, additionalInfo: additionalInfo, required: required, helpText: helpText }), _jsx("div", { className: 'flex', children: _jsx(Autocomplete, { id: inputId, className: styles.select, disablePortal: disablePortal, value: getOpt(value || ''), filterOptions: filterOptions, isOptionEqualToValue: (option, value) => {
                        // Don't match loading indicator
                        if (option.isLoadingIndicator || value.isLoadingIndicator) {
                            return false;
                        }
                        return option.value === value.value;
                    }, onChange: handleSelect, options: enhancedOptions, getOptionLabel: option => {
                        // Don't show loading indicator in the input
                        if (option.isLoadingIndicator) {
                            return '';
                        }
                        if (getOpt(value || '') === option) {
                            return getOptionSelectedLabel(option) || '';
                        }
                        return option.label || '';
                    }, disabled: disabled, renderOption: customRenderOption, renderInput: params => (_jsx(TextField, { placeholder: value ? undefined : placeholder, ...params, InputProps: {
                            ...params.InputProps,
                            ...(showSearchIcon && {
                                startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx("div", { className: styles.searchIconWrapper, children: _jsx(SearchIcon, { color: 'primary', size: 24 }) }) })),
                            }),
                        }, inputProps: {
                            ...params.inputProps,
                            className: classNames(params.inputProps?.className, {
                                'weglot-exclude': disableWeglot,
                            }),
                        } })), noOptionsText: t('No options available'), loading: loading, ListboxProps: {
                        className: classNames({
                            'weglot-exclude': disableWeglot,
                        }),
                        onScroll: event => {
                            const listboxNode = event.currentTarget;
                            if (onScrollEnd &&
                                !paginationLoading && // Don't trigger if already loading
                                listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
                                onScrollEnd();
                            }
                        },
                    }, onInputChange: (_, value, reason) => {
                        if (onSearch && (reason === 'input' || reason === 'clear')) {
                            onSearch(value);
                        }
                    }, inputValue: inputValue }) }), _jsx(ErrorList, { show: !!errors, errors: errors })] }));
};
